<template>
  <div class="row mb-3">
    <div>
      <div class="main-card card">
        <div class="card-header text-left text-unset">
          {{ $t("withdraw.toBtc") }}
        </div>
        <div class="card-body">
          <div
            class="card-text fsize-1 mb-3"
            v-html="$t('withdraw.btc-note')"
          ></div>
          <div>
            <b-form-group class="mb-3">
              <div class="currency-select-v2-wrapper">
                <v-select
                  v-model="selectedCurrencyObj"
                  :options="currencyOptions"
                  label="label"
                  :reduce="(opt) => opt.value"
                  :placeholder="$t('withdraw.selectToken')"
                  :clearable="false"
                  :searchable="false"
                  class="currency-select-v2"
                >
                  <template #option="option">
                    <div class="currency-option">
                      <span class="currency-opt-label">{{ option.label }}</span>
                    </div>
                  </template>
                  <template
                    #selected-option-container="{ option, placeholder }"
                  >
                    <div class="selected-value-container">
                      <template
                        v-if="
                          option && typeof option === 'object' && option.label
                        "
                      >
                        <span class="currency-opt-label">{{
                          option.label
                        }}</span>
                      </template>
                      <template
                        v-else-if="
                          option &&
                          typeof option === 'string' &&
                          currencyOptions.find((c) => c.value === option)
                        "
                      >
                        <span class="currency-opt-label">
                          {{
                            currencyOptions.find((c) => c.value === option)
                              .label
                          }}
                        </span>
                      </template>
                      <span v-else class="placeholder-text">
                        {{ placeholder }}
                      </span>
                    </div>
                  </template>
                </v-select>
              </div>
            </b-form-group>

            <div class="input-group">
              <div class="input-group-prepend">
                <span
                  class="input-group-text pe-7s-wallet"
                  style="line-height: 1.5"
                ></span>
              </div>
              <input
                :placeholder="fixedAddressPlaceholder"
                type="text"
                class="form-control"
                v-model="address"
                :disabled="!selectedCurrencyCode"
              />
            </div>
            <br />
            <div class="input-group">
              <input
                :placeholder="$t('common.withdraw-amount')"
                step="0.01"
                type="number"
                class="form-control"
                v-model="amount"
                :disabled="!selectedCurrencyCode"
              />
              <div class="input-group-append">
                <span class="input-group-text">{{ amountCurrencyLabel }}</span>
              </div>
            </div>
          </div>

          <div class="mt-1">
            <v-btn
              color="primary"
              class="ma-0 text-unset"
              @click="requestWithdrawal"
              :disabled="submitDisabled"
              small
            >
              {{ $t("withdraw.request") }}
            </v-btn>
            <br />
            <span class="red--text" v-if="!kyc">{{
              $t("withdraw.no-kyc")
            }}</span>
          </div>
        </div>
      </div>
    </div>
    <v-snackbar
      v-model="success"
      :timeout="timeout"
      color="primary"
      absolute
      right
      rounded="pill"
    >
      {{ $t("withdraw.requested", [lastAmount, payoutForMessage]) }}

      <template v-slot:action="{ attrs }">
        <v-btn
          color="blue"
          text
          v-bind="attrs"
          @click="success = false"
          class="text-unset"
        >
          {{ $t("close") }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import gql from "graphql-tag";
import vSelect from "vue-select";

export default {
  components: { vSelect },
  props: ["kyc", "maxHtl", "maxUsd"],
  data() {
    return {
      address: null,
      success: false,
      amount: 0,
      timeout: 2000,
      lastAmount: 0,
      lastCurrency: null,
      selectedCurrencyObj: null,
      currencyOptions: [
        { value: "HTL", label: this.$t("withdraw.available", ["HTL"]) },
        { value: "USDC", label: this.$t("withdraw.available", ["USDC"]) },
      ],
    };
  },
  computed: {
    ...mapGetters({
      htlUsd: "prices/htlUsd",
      htlBtc: "prices/htlBtc",
      btcUsd: "prices/btcUsd",
    }),
    selectedCurrencyCode() {
      if (!this.selectedCurrencyObj) return null;
      const found = this.currencyOptions.find(
        (c) =>
          c.value === this.selectedCurrencyObj || c === this.selectedCurrencyObj
      );
      return found ? found.value : null;
    },
    amountCurrencyLabel() {
      return this.selectedCurrencyCode || "HTL";
    },
    limitInSelectedCurrency() {
      if (!this.selectedCurrencyCode) return null;
      if (this.selectedCurrencyCode === "HTL") {
        return this.maxHtl != null ? Math.floor(this.maxHtl * 100) / 100 : null;
      }
      if (this.selectedCurrencyCode === "USDC") {
        return this.maxUsd != null ? Math.floor(this.maxUsd * 100) / 100 : null;
      }
      return null;
    },
    fixedAddressPlaceholder() {
      const tpl = this.$t("withdraw.address");
      if (typeof tpl !== "string") return "BTC " + tpl;
      return tpl.replace(/\{0\}/g, "BTC");
    },
    submitDisabled() {
      if (!this.kyc) return true;
      if (!this.selectedCurrencyCode) return true;
      const amt = parseFloat(this.amount);
      if (!isFinite(amt) || amt <= 0) return true;
      if (!this.address || !String(this.address).trim()) return true;
      return false;
    },
    payoutForMessage() {
      return this.lastCurrency || "HTL";
    },
  },
  watch: {
    selectedCurrencyCode(val) {
      if (!val) return;
      // Ob menjavi valute vedno nastavimo limit nove izbrane valute
      const lim = this.limitInSelectedCurrency;
      if (lim != null) {
        this.amount = lim;
      } else {
        this.amount = 0;
      }
    },
    maxHtl() {
      if (this.selectedCurrencyCode === "HTL") {
        this.updateAmountToLimit();
      }
    },
    maxUsd() {
      if (this.selectedCurrencyCode === "USDC") {
        this.updateAmountToLimit();
      }
    },
    htlUsd() {
      if (!this.selectedCurrencyCode) return;
      this.updateAmountToLimit();
    },
  },
  mounted() {
    this.updateAmountToLimit();
  },
  methods: {
    updateAmountToLimit() {
      const lim = this.limitInSelectedCurrency;
      if (lim != null) {
        if (
          this.amount == null ||
          isNaN(this.amount) ||
          this.amount <= 0 ||
          this.amount > lim
        ) {
          this.amount = lim;
        }
      } else if (
        this.amount == null ||
        isNaN(this.amount) ||
        this.amount <= 0
      ) {
        this.amount = 0;
      }
    },
    async requestWithdrawal() {
      if (!this.selectedCurrencyCode) return;
      const amt = parseFloat(this.amount);
      if (!isFinite(amt) || amt <= 0) return;
      const address = this.address ? String(this.address).trim() : "";
      if (!address) return;

      const rates =
        this.selectedCurrencyCode === "HTL"
          ? { in: 1, out: 1 }
          : {
              in:
                isFinite(parseFloat(this.htlUsd)) && parseFloat(this.htlUsd) > 0
                  ? parseFloat(1 / this.htlUsd)
                  : 1,
              out:
                isFinite(parseFloat(this.htlUsd)) && parseFloat(this.htlUsd) > 0
                  ? parseFloat(1 / this.htlUsd)
                  : 1,
            };

      await this.$apollo.mutate({
        mutation: gql`
          mutation (
            $address: String!
            $amount: Float!
            $currency: String!
            $currencyFrom: String!
            $rates: InOutRates
          ) {
            requestWithdrawal(
              amount: $amount
              address: $address
              currency: $currency
              currencyFrom: $currencyFrom
              rates: $rates
            )
          }
        `,
        variables: {
          amount: amt,
          address: address,
          currency: "BTC",
          currencyFrom: this.selectedCurrencyCode,
          rates: rates,
        },
      });

      this.lastAmount = amt;
      this.lastCurrency = this.selectedCurrencyCode;
      this.success = true;
      setTimeout(() => {
        this.$router.go(0);
      }, Math.max(this.timeout, 2000));
    },
  },
};
</script>

<style scoped>
.currency-select-v2-wrapper {
  width: 100%;
}

.currency-select-v2 {
  display: block;
  width: 100%;
  max-width: 450px;
}

::v-deep .v-select .dropdown-toggle {
  min-height: 52px;
  align-items: center;
}

::v-deep .v-select .vs__selected-options {
  margin-top: 7px;
}

::v-deep .vs__search {
  margin: 0 !important;
  padding: 0 !important;
}

.selected-value-container {
  display: inline-flex;
  align-items: center;
  height: 100%;
}

.placeholder-text {
  color: #6c757d;
  font-size: 16px;
}

.currency-option {
  display: flex;
  align-items: center;
  min-height: 40px;
}

.currency-opt-label {
  padding-left: 10px;
  font-size: 18px;
  line-height: 1;
}

::v-deep .vs__dropdown-option {
  padding: 8px 12px;
  display: flex;
  align-items: center;
  min-height: 48px;
}

::v-deep .vs__dropdown-menu {
  max-height: 300px;
  overflow-y: auto;
}
</style>